'use client';

import { c as _c } from "react/compiler-runtime";
import { ArrowUpRight, BywayFavorite, CheckCircleSolid, CoffeeCupSmall, MapPinAlt, NavArrowDown, StayPictogram } from '@byway/icons';
import clsx from 'clsx';
import { useTranslations } from 'next-intl';
import { Fragment, useId, useState } from 'react';
import { Divider } from '~/components/Divider';
import { ListingCard } from '~/components/ListingCard';
import { AlternativeAccommodationDrawer } from '~/components/TripOverview/accommodationPage/AlternativeAccommodationDrawer';
import { ChangeRoomDrawer } from '~/components/TripOverview/accommodationPage/ChangeRoomDrawer';
import { formatStationDistanceMeters, humanizeAccommodationAttribute } from '~/components/TripOverview/accommodationPage/formatStationDistance';
import { usePrefetchAccommodationDrawersOnInView } from '~/components/TripOverview/accommodationPage/usePrefetchAccommodationDrawersOnInView';
import { useTripOverviewSummary } from '~/components/TripOverview/hooks/useTripOverviewSummary';
import { TRPC_SETTINGS } from '~/constants/trpc';
import { useIsTripUpdating } from '~/hooks/useIsTripUpdating';
import { api } from '~/libs/api';
import type { TripAccommodationStay } from '~/services/engine-ts';
import { isBywayFavouriteAccommodation } from '~/utils/accommodationHelpers';
import { trackFunnelEvent } from '~/utils/funnelTracking';
import styles from './AccommodationCard.module.css';
import type { AccommodationCardVariant } from './AccommodationCard.types';
import { AccommodationCheckInOutSection } from './AccommodationCheckInOutSection';
import { getAccommodationCheckInOutDetails } from './accommodationCheckInOut';
import { ItineraryCard } from './ItineraryCard';
type AccommodationDetails = {
  accommodation: {
    title: string;
    roomName: string;
    boardBasis?: string;
  };
};
const INITIAL_VISIBLE_AMENITIES = 2;
const BODY_TRUNCATE_AT = 200;
function AmenityItem(t0) {
  const $ = _c(5);
  const {
    attribute
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <span className={styles.includeIcon} aria-hidden={true}><CheckCircleSolid size="small" decorative={true} /></span>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== attribute) {
    t2 = humanizeAccommodationAttribute(attribute);
    $[1] = attribute;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t2) {
    t3 = <div className={styles.includeItem}>{t1}{t2}</div>;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
}
type StayCheckInOutDetails = ReturnType<typeof getAccommodationCheckInOutDetails>;
const getPreviewBodyText = (bodyText: string) => {
  const slice = bodyText.slice(0, BODY_TRUNCATE_AT);
  const lastSpace = slice.lastIndexOf(' ');
  return `${bodyText.slice(0, lastSpace > 0 ? lastSpace : BODY_TRUNCATE_AT)}\u2026`;
};
type StayMetaBlockProps = {
  arrivalDistance: string | null;
  departureDistance: string | null;
  showBreakfast: boolean;
};
function StayMetaBlock(t0) {
  const $ = _c(11);
  const {
    arrivalDistance,
    departureDistance,
    showBreakfast
  } = t0;
  const t = useTranslations("journey");
  if (!arrivalDistance && !departureDistance && !showBreakfast) {
    return null;
  }
  let t1;
  if ($[0] !== arrivalDistance) {
    t1 = arrivalDistance ? <div className={styles.metaRow}><span className={styles.metaIcon} aria-hidden={true}><MapPinAlt size="small" decorative={true} /></span><p className={styles.metaText}>{arrivalDistance}</p></div> : null;
    $[0] = arrivalDistance;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== departureDistance) {
    t2 = departureDistance ? <div className={styles.metaRow}><span className={styles.metaIcon} aria-hidden={true}><MapPinAlt size="small" decorative={true} /></span><p className={styles.metaText}>{departureDistance}</p></div> : null;
    $[2] = departureDistance;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== showBreakfast || $[5] !== t) {
    t3 = showBreakfast ? <div className={styles.metaRow}><span className={styles.metaIcon} aria-hidden={true}><CoffeeCupSmall size="small" decorative={true} /></span><p className={styles.metaText}>{t("breakfast_included")}</p></div> : null;
    $[4] = showBreakfast;
    $[5] = t;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t1 || $[8] !== t2 || $[9] !== t3) {
    t4 = <div className={styles.metaBlock}>{t1}{t2}{t3}</div>;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  return t4;
}
type StayBodySectionProps = {
  bodyText: string;
  isFoldExpanded: boolean;
  bodyOverflowId: string;
};
function StayBodySection(t0) {
  const $ = _c(24);
  const {
    bodyText,
    isFoldExpanded,
    bodyOverflowId
  } = t0;
  if (!bodyText) {
    return null;
  }
  if (bodyText.length <= BODY_TRUNCATE_AT) {
    let t1;
    if ($[0] !== bodyText) {
      t1 = <div className={styles.bodySection}><ItineraryCard.Body text={bodyText} disableTruncate={true} /></div>;
      $[0] = bodyText;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  const t1 = !isFoldExpanded && styles.foldExpandableOpen;
  let t2;
  if ($[2] !== t1) {
    t2 = clsx(styles.foldExpandable, t1);
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== bodyText) {
    t3 = getPreviewBodyText(bodyText);
    $[4] = bodyText;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t3) {
    t4 = <div className={styles.foldExpandableInner}><ItineraryCard.Body text={t3} disableTruncate={true} /></div>;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== isFoldExpanded || $[9] !== t2 || $[10] !== t4) {
    t5 = <div className={t2} inert={isFoldExpanded}>{t4}</div>;
    $[8] = isFoldExpanded;
    $[9] = t2;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const t6 = isFoldExpanded && styles.foldExpandableOpen;
  let t7;
  if ($[12] !== t6) {
    t7 = clsx(styles.foldExpandable, t6);
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const t8 = !isFoldExpanded;
  let t9;
  if ($[14] !== bodyText) {
    t9 = <div className={styles.foldExpandableInner}><ItineraryCard.Body text={bodyText} disableTruncate={true} /></div>;
    $[14] = bodyText;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== bodyOverflowId || $[17] !== t7 || $[18] !== t8 || $[19] !== t9) {
    t10 = <div id={bodyOverflowId} className={t7} inert={t8}>{t9}</div>;
    $[16] = bodyOverflowId;
    $[17] = t7;
    $[18] = t8;
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== t10 || $[22] !== t5) {
    t11 = <div className={styles.bodySection}>{t5}{t10}</div>;
    $[21] = t10;
    $[22] = t5;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  return t11;
}
type StayRestSectionProps = {
  websiteUrl: string | undefined;
  checkInOutDetails: StayCheckInOutDetails;
  isFoldExpanded: boolean;
  restOverflowId: string;
};
function StayRestSection(t0) {
  const $ = _c(15);
  const {
    websiteUrl,
    checkInOutDetails,
    isFoldExpanded,
    restOverflowId
  } = t0;
  const t = useTranslations("journey");
  if (!websiteUrl && !checkInOutDetails) {
    return null;
  }
  const t1 = isFoldExpanded && styles.foldExpandableOpen;
  let t2;
  if ($[0] !== t1) {
    t2 = clsx(styles.foldExpandable, t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t3 = !isFoldExpanded;
  let t4;
  if ($[2] !== t || $[3] !== websiteUrl) {
    t4 = websiteUrl ? <a className={styles.externalLink} href={websiteUrl} target="_blank" rel="noopener noreferrer">{t("accommodation_website_link")}<ArrowUpRight size="small" decorative={true} /></a> : null;
    $[2] = t;
    $[3] = websiteUrl;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== checkInOutDetails) {
    t5 = checkInOutDetails ? <AccommodationCheckInOutSection details={checkInOutDetails} /> : null;
    $[5] = checkInOutDetails;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== t4 || $[8] !== t5) {
    t6 = <div className={styles.foldExpandableInner}>{t4}{t5}</div>;
    $[7] = t4;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== restOverflowId || $[11] !== t2 || $[12] !== t3 || $[13] !== t6) {
    t7 = <div id={restOverflowId} className={t2} inert={t3}>{t6}</div>;
    $[10] = restOverflowId;
    $[11] = t2;
    $[12] = t3;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  return t7;
}
type StayFoldProps = {
  bodyText: string;
  websiteUrl: string | undefined;
  checkInOutDetails: StayCheckInOutDetails;
  defaultExpanded: boolean;
};
function StayFold({
  bodyText,
  websiteUrl,
  checkInOutDetails,
  defaultExpanded
}: Readonly<StayFoldProps>) {
  const tCommon = useTranslations('common');
  const bodyOverflowId = useId();
  const restOverflowId = useId();
  const [isFoldExpanded, setIsFoldExpanded] = useState(defaultExpanded);
  const isBodyTruncatable = Boolean(bodyText) && bodyText.length > BODY_TRUNCATE_AT;
  const hasRestContent = Boolean(websiteUrl || checkInOutDetails);
  const hasFoldableContent = isBodyTruncatable || hasRestContent;
  const ariaControls = [isBodyTruncatable ? bodyOverflowId : null, hasRestContent ? restOverflowId : null].filter(Boolean).join(' ') || undefined;
  return <>
      <div className={styles.foldGroup}>
        <StayBodySection bodyText={bodyText} isFoldExpanded={isFoldExpanded} bodyOverflowId={bodyOverflowId} />
        <StayRestSection websiteUrl={websiteUrl} checkInOutDetails={checkInOutDetails} isFoldExpanded={isFoldExpanded} restOverflowId={restOverflowId} />
      </div>

      {hasFoldableContent ? <button type="button" className={styles.textLinkButton} aria-expanded={isFoldExpanded} aria-controls={ariaControls} onClick={() => setIsFoldExpanded(open => !open)}>
          {isFoldExpanded ? tCommon('read_less') : tCommon('read_more')}
          <span className={clsx(styles.textLinkButtonIcon, isFoldExpanded && styles.textLinkButtonIconExpanded)} aria-hidden>
            <NavArrowDown size="small" decorative />
          </span>
        </button> : null}
    </>;
}
function StayAmenities(t0) {
  const $ = _c(33);
  const {
    attributes
  } = t0;
  const t = useTranslations("journey");
  const amenitiesExpandId = useId();
  const [showAllAmenities, setShowAllAmenities] = useState(false);
  if (attributes.length === 0) {
    return null;
  }
  let hasMoreAmenities;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  if ($[0] !== amenitiesExpandId || $[1] !== attributes || $[2] !== showAllAmenities || $[3] !== t) {
    const initialAmenities = attributes.slice(0, INITIAL_VISIBLE_AMENITIES);
    const extraAmenities = attributes.slice(INITIAL_VISIBLE_AMENITIES);
    hasMoreAmenities = extraAmenities.length > 0;
    if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <Divider variant="horizontal" />;
      $[11] = t6;
    } else {
      t6 = $[11];
    }
    t4 = styles.includesSection;
    let t7;
    if ($[12] !== t) {
      t7 = t("accommodation_includes_heading");
      $[12] = t;
      $[13] = t7;
    } else {
      t7 = $[13];
    }
    if ($[14] !== t7) {
      t5 = <p className={styles.includesTitle}>{t7}</p>;
      $[14] = t7;
      $[15] = t5;
    } else {
      t5 = $[15];
    }
    t1 = styles.includesItems;
    t2 = initialAmenities.map(_temp2);
    t3 = hasMoreAmenities ? <div id={amenitiesExpandId} className={clsx(styles.includesItemsExpandable, showAllAmenities && styles.includesItemsExpandableOpen)}><div className={styles.includesItemsExpandableInner}>{extraAmenities.map(_temp3)}</div></div> : null;
    $[0] = amenitiesExpandId;
    $[1] = attributes;
    $[2] = showAllAmenities;
    $[3] = t;
    $[4] = hasMoreAmenities;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
    $[10] = t6;
  } else {
    hasMoreAmenities = $[4];
    t1 = $[5];
    t2 = $[6];
    t3 = $[7];
    t4 = $[8];
    t5 = $[9];
    t6 = $[10];
  }
  let t7;
  if ($[16] !== t1 || $[17] !== t2 || $[18] !== t3) {
    t7 = <div className={t1}>{t2}{t3}</div>;
    $[16] = t1;
    $[17] = t2;
    $[18] = t3;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== amenitiesExpandId || $[21] !== hasMoreAmenities || $[22] !== showAllAmenities || $[23] !== t) {
    t8 = hasMoreAmenities ? <button type="button" className={styles.textLinkButton} aria-expanded={showAllAmenities} aria-controls={amenitiesExpandId} onClick={() => setShowAllAmenities(_temp4)}>{showAllAmenities ? t("accommodation_amenities_see_less") : t("accommodation_filters_see_all")}<span className={clsx(styles.textLinkButtonIcon, showAllAmenities && styles.textLinkButtonIconExpanded)} aria-hidden={true}><NavArrowDown size="small" decorative={true} /></span></button> : null;
    $[20] = amenitiesExpandId;
    $[21] = hasMoreAmenities;
    $[22] = showAllAmenities;
    $[23] = t;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  let t9;
  if ($[25] !== t4 || $[26] !== t5 || $[27] !== t7 || $[28] !== t8) {
    t9 = <div className={t4}>{t5}{t7}{t8}</div>;
    $[25] = t4;
    $[26] = t5;
    $[27] = t7;
    $[28] = t8;
    $[29] = t9;
  } else {
    t9 = $[29];
  }
  let t10;
  if ($[30] !== t6 || $[31] !== t9) {
    t10 = <>{t6}{t9}</>;
    $[30] = t6;
    $[31] = t9;
    $[32] = t10;
  } else {
    t10 = $[32];
  }
  return t10;
}
function _temp4(open) {
  return !open;
}
function _temp3(attribute_0) {
  return <AmenityItem key={attribute_0} attribute={attribute_0} />;
}
function _temp2(attribute) {
  return <AmenityItem key={attribute} attribute={attribute} />;
}
type AccommodationCardStayContentProps = {
  placeTitle: string;
  images: Parameters<typeof ItineraryCard.Gallery>[0]['images'];
  variant: AccommodationCardVariant;
  accommodationTitle: string | undefined;
  rooms: Array<{
    id: string;
    displayName: string;
  }>;
  accommodationDetails: AccommodationDetails | null;
  bodyText: string;
  accommodationStay?: TripAccommodationStay;
  arrivalDistance: string | null;
  departureDistance: string | null;
  showBreakfast: boolean;
  attributes: string[];
  tripId: string;
  itemId: number;
};
export function AccommodationCardStayContent(t0) {
  const $ = _c(95);
  const {
    placeTitle,
    images,
    variant,
    accommodationTitle,
    rooms,
    accommodationDetails,
    bodyText,
    accommodationStay,
    arrivalDistance,
    departureDistance,
    showBreakfast,
    attributes,
    tripId,
    itemId
  } = t0;
  const t = useTranslations("journey");
  const [isAlternativesOpen, setIsAlternativesOpen] = useState(false);
  const [roomDrawerIndex, setRoomDrawerIndex] = useState(null);
  let t1;
  if ($[0] !== itemId || $[1] !== tripId) {
    t1 = {
      tripId,
      itemId
    };
    $[0] = itemId;
    $[1] = tripId;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const {
    ref: prefetchRef,
    prefetchRooms
  } = usePrefetchAccommodationDrawersOnInView(t1);
  const {
    canEditTripSelections
  } = useTripOverviewSummary();
  const isTripUpdating = useIsTripUpdating();
  const websiteUrl = accommodationStay?.accommodation.websiteUrl;
  let t2;
  if ($[3] !== accommodationStay) {
    t2 = getAccommodationCheckInOutDetails(accommodationStay);
    $[3] = accommodationStay;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const checkInOutDetails = t2;
  const t3 = accommodationStay?.accommodation.ranking ?? 0;
  let t4;
  if ($[5] !== t3) {
    t4 = isBywayFavouriteAccommodation(t3);
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const isFavourite = t4;
  const accommodationValue = accommodationDetails?.accommodation.title ?? accommodationTitle ?? "";
  const hasMultipleRooms = rooms.length > 1;
  let t5;
  if ($[7] !== tripId) {
    t5 = {
      tripId
    };
    $[7] = tripId;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const t6 = Boolean(tripId);
  let t7;
  if ($[9] !== t6) {
    t7 = {
      ...TRPC_SETTINGS.QUERY_NO_REFETCH,
      enabled: t6
    };
    $[9] = t6;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const {
    data: accommodationData
  } = api.trip.getAccommodation.useQuery(t5, t7);
  const maxArrivalDistanceMeters = accommodationData?.appliedPreferences?.maxDistanceFromArrivalStationMeters ?? null;
  const arrivalDistanceMeters = accommodationStay?.accommodation.distanceFromArrivalStationMeters ?? null;
  const departureDistanceMeters = accommodationStay?.accommodation.distanceFromDepartureStationMeters ?? null;
  const measuredDistanceMeters = arrivalDistanceMeters ?? departureDistanceMeters;
  const isDistancePreferenceUnmet = maxArrivalDistanceMeters !== null && measuredDistanceMeters !== null && measuredDistanceMeters > maxArrivalDistanceMeters;
  const distancePreferenceUnmetKey = arrivalDistanceMeters === null ? "accommodation_distance_preference_unmet_departure" : "accommodation_distance_preference_unmet";
  let t8;
  if ($[11] !== t) {
    t8 = t("includes_accommodation");
    $[11] = t;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = () => {
      trackFunnelEvent("trip_funnel_find_more_hotels");
      setIsAlternativesOpen(true);
    };
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  const t10 = !canEditTripSelections || isTripUpdating;
  let t11;
  if ($[14] !== accommodationValue || $[15] !== t10 || $[16] !== t8) {
    t11 = <ItineraryCard.ChangeField label={t8} value={accommodationValue} onPress={t9} isDisabled={t10} />;
    $[14] = accommodationValue;
    $[15] = t10;
    $[16] = t8;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  let t12;
  if ($[18] !== canEditTripSelections || $[19] !== hasMultipleRooms || $[20] !== isTripUpdating || $[21] !== prefetchRooms || $[22] !== rooms || $[23] !== t) {
    let t13;
    if ($[25] !== canEditTripSelections || $[26] !== hasMultipleRooms || $[27] !== isTripUpdating || $[28] !== prefetchRooms || $[29] !== t) {
      t13 = (room, index) => <Fragment key={`traveller-mix-${index}`}><Divider variant="horizontal" /><ItineraryCard.ChangeField label={hasMultipleRooms ? t("room_number", {
          number: index + 1
        }) : t("accommodation_field_room")} value={room.displayName} onPress={() => {
          trackFunnelEvent("trip_funnel_edit_room", {
            room_number: index + 1
          });
          setRoomDrawerIndex(index);
        }} onPrefetch={prefetchRooms} isDisabled={!canEditTripSelections || isTripUpdating} /></Fragment>;
      $[25] = canEditTripSelections;
      $[26] = hasMultipleRooms;
      $[27] = isTripUpdating;
      $[28] = prefetchRooms;
      $[29] = t;
      $[30] = t13;
    } else {
      t13 = $[30];
    }
    t12 = rooms.map(t13);
    $[18] = canEditTripSelections;
    $[19] = hasMultipleRooms;
    $[20] = isTripUpdating;
    $[21] = prefetchRooms;
    $[22] = rooms;
    $[23] = t;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[31] !== t11 || $[32] !== t12) {
    t13 = <>{t11}{t12}</>;
    $[31] = t11;
    $[32] = t12;
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  const changeFields = t13;
  let t14;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <StayPictogram size="small" decorative={true} />;
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  const t15 = `Stay in ${placeTitle}`;
  let t16;
  if ($[35] !== t15) {
    t16 = <ItineraryCard.Header icon={t14} title={t15} />;
    $[35] = t15;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  let t17;
  if ($[37] !== images || $[38] !== isFavourite || $[39] !== t) {
    t17 = images.length > 0 ? <ItineraryCard.Gallery images={images} tag={isFavourite ? <ListingCard.Tag icon={<BywayFavorite decorative={true} size="small" />}>{t("byway_favourite")}</ListingCard.Tag> : undefined} /> : null;
    $[37] = images;
    $[38] = isFavourite;
    $[39] = t;
    $[40] = t17;
  } else {
    t17 = $[40];
  }
  let t18;
  if ($[41] !== accommodationTitle) {
    t18 = accommodationTitle ? <ItineraryCard.Subheading>{accommodationTitle}</ItineraryCard.Subheading> : null;
    $[41] = accommodationTitle;
    $[42] = t18;
  } else {
    t18 = $[42];
  }
  let t19;
  if ($[43] !== distancePreferenceUnmetKey || $[44] !== isDistancePreferenceUnmet || $[45] !== maxArrivalDistanceMeters || $[46] !== t) {
    t19 = isDistancePreferenceUnmet ? <ItineraryCard.InfoBanner variant="filled">{t(distancePreferenceUnmetKey, {
        distance: formatStationDistanceMeters(maxArrivalDistanceMeters)
      })}</ItineraryCard.InfoBanner> : null;
    $[43] = distancePreferenceUnmetKey;
    $[44] = isDistancePreferenceUnmet;
    $[45] = maxArrivalDistanceMeters;
    $[46] = t;
    $[47] = t19;
  } else {
    t19 = $[47];
  }
  let t20;
  if ($[48] !== accommodationValue || $[49] !== changeFields) {
    t20 = accommodationValue ? <div className={styles.changeFieldsBox}>{changeFields}</div> : null;
    $[48] = accommodationValue;
    $[49] = changeFields;
    $[50] = t20;
  } else {
    t20 = $[50];
  }
  let t21;
  if ($[51] !== arrivalDistance || $[52] !== departureDistance || $[53] !== showBreakfast) {
    t21 = <StayMetaBlock arrivalDistance={arrivalDistance} departureDistance={departureDistance} showBreakfast={showBreakfast} />;
    $[51] = arrivalDistance;
    $[52] = departureDistance;
    $[53] = showBreakfast;
    $[54] = t21;
  } else {
    t21 = $[54];
  }
  const t22 = variant === "stay-expanded";
  let t23;
  if ($[55] !== bodyText || $[56] !== checkInOutDetails || $[57] !== t22 || $[58] !== websiteUrl) {
    t23 = <StayFold bodyText={bodyText} websiteUrl={websiteUrl} checkInOutDetails={checkInOutDetails} defaultExpanded={t22} />;
    $[55] = bodyText;
    $[56] = checkInOutDetails;
    $[57] = t22;
    $[58] = websiteUrl;
    $[59] = t23;
  } else {
    t23 = $[59];
  }
  let t24;
  if ($[60] !== attributes) {
    t24 = <StayAmenities attributes={attributes} />;
    $[60] = attributes;
    $[61] = t24;
  } else {
    t24 = $[61];
  }
  let t25;
  if ($[62] !== t18 || $[63] !== t19 || $[64] !== t20 || $[65] !== t21 || $[66] !== t23 || $[67] !== t24) {
    t25 = <ItineraryCard.Content><div className={styles.expandedStay}>{t18}{t19}{t20}{t21}{t23}{t24}</div></ItineraryCard.Content>;
    $[62] = t18;
    $[63] = t19;
    $[64] = t20;
    $[65] = t21;
    $[66] = t23;
    $[67] = t24;
    $[68] = t25;
  } else {
    t25 = $[68];
  }
  let t26;
  if ($[69] !== prefetchRef || $[70] !== t16 || $[71] !== t17 || $[72] !== t25) {
    t26 = <ItineraryCard ref={prefetchRef}>{t16}{t17}{t25}</ItineraryCard>;
    $[69] = prefetchRef;
    $[70] = t16;
    $[71] = t17;
    $[72] = t25;
    $[73] = t26;
  } else {
    t26 = $[73];
  }
  let t27;
  if ($[74] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = () => setIsAlternativesOpen(false);
    $[74] = t27;
  } else {
    t27 = $[74];
  }
  const t28 = accommodationStay?.accommodation.distanceFromArrivalStationMeters;
  const t29 = accommodationStay?.accommodation.distanceFromDepartureStationMeters;
  let t30;
  if ($[75] !== t28 || $[76] !== t29) {
    t30 = {
      distanceFromArrivalStationMeters: t28,
      distanceFromDepartureStationMeters: t29
    };
    $[75] = t28;
    $[76] = t29;
    $[77] = t30;
  } else {
    t30 = $[77];
  }
  let t31;
  if ($[78] !== isAlternativesOpen || $[79] !== itemId || $[80] !== placeTitle || $[81] !== t30 || $[82] !== tripId) {
    t31 = <AlternativeAccommodationDrawer isOpen={isAlternativesOpen} close={t27} tripId={tripId} itemId={itemId} placeName={placeTitle} currentStationDistances={t30} />;
    $[78] = isAlternativesOpen;
    $[79] = itemId;
    $[80] = placeTitle;
    $[81] = t30;
    $[82] = tripId;
    $[83] = t31;
  } else {
    t31 = $[83];
  }
  const t32 = roomDrawerIndex !== null;
  let t33;
  if ($[84] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = () => setRoomDrawerIndex(null);
    $[84] = t33;
  } else {
    t33 = $[84];
  }
  const t34 = roomDrawerIndex ?? 0;
  let t35;
  if ($[85] !== itemId || $[86] !== placeTitle || $[87] !== t32 || $[88] !== t34 || $[89] !== tripId) {
    t35 = <ChangeRoomDrawer isOpen={t32} close={t33} tripId={tripId} itemId={itemId} roomIndex={t34} placeName={placeTitle} />;
    $[85] = itemId;
    $[86] = placeTitle;
    $[87] = t32;
    $[88] = t34;
    $[89] = tripId;
    $[90] = t35;
  } else {
    t35 = $[90];
  }
  let t36;
  if ($[91] !== t26 || $[92] !== t31 || $[93] !== t35) {
    t36 = <>{t26}{t31}{t35}</>;
    $[91] = t26;
    $[92] = t31;
    $[93] = t35;
    $[94] = t36;
  } else {
    t36 = $[94];
  }
  return t36;
}